import React, { useContext, useState, useEffect } from "react";
import { useNavigate, Outlet } from "react-router-dom";
import { AuthContext } from "../contexts/AuthContext";
import LoadingOverlay from "./LoadingOverlay";
import NavbarWithWebSocket from "./NavbarWithWebSocket";
import Sidebar2 from "./Sidebar2";
import AlertBanner from "./AlertBanner";
import axios from "axios";
import { BASE_URL } from "../config";
import Footer from "./Footer";
import GlobalSplitViewLayout from "./GlobalSplitViewLayout";

const SIDEBAR_COLLAPSED_WIDTH = 80;
const SIDEBAR_DEFAULT_EXPANDED_WIDTH = 320;
const SIDEBAR_MIN_EXPANDED_WIDTH = 320;
const SIDEBAR_MAX_EXPANDED_WIDTH = 520;

const clampSidebarWidth = (width) =>
  Math.min(
    SIDEBAR_MAX_EXPANDED_WIDTH,
    Math.max(SIDEBAR_MIN_EXPANDED_WIDTH, width)
  );

const MainLayout = () => {
  const navigate = useNavigate();
  const { isLoading, isAuthenticated } = useContext(AuthContext);
  const [showLoading, setShowLoading] = useState(false);
  // BANNERS ALERT CODE:
  const [banners, setBanners] = useState([]);
  const [visibleBanners, setVisibleBanners] = useState({});
  // Initialize sidebar state from localStorage or default to true
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("isSidebarOpen");
    return savedState !== null ? JSON.parse(savedState) : true;
  });
  const [sidebarExpandedWidth, setSidebarExpandedWidth] = useState(() => {
    const savedWidth = Number(localStorage.getItem("sidebarExpandedWidth"));
    return Number.isFinite(savedWidth)
      ? clampSidebarWidth(savedWidth)
      : SIDEBAR_DEFAULT_EXPANDED_WIDTH;
  });

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem("isSidebarOpen", JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  useEffect(() => {
    localStorage.setItem(
      "sidebarExpandedWidth",
      String(clampSidebarWidth(sidebarExpandedWidth))
    );
  }, [sidebarExpandedWidth]);

  // Add delay to loading overlay
  useEffect(() => {
    if (isLoading) {
      setShowLoading(true);
    } else {
      const timer = setTimeout(() => {
        setShowLoading(false);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  // Fetch banners
  useEffect(() => {
    // if (!isAuthenticated) return; // Skip API call if not authenticated
    axios
      .get(`${BASE_URL}/banners-alerts`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(
            "authentication/token"
          )}`,
        },
      })
      .then((response) => {

        setBanners(response.data);
        const initialVisibility = {};
        response.data.forEach((banner) => {
          initialVisibility[banner.id] = true;
        });
        setVisibleBanners(initialVisibility);
      })
      .catch((error) => console.error("Error fetching banners:", error));
  }, [isAuthenticated]);

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);
  const expandSidebar = () => setIsSidebarOpen(true);
  const handleSidebarWidthChange = (nextWidth) => {
    setSidebarExpandedWidth(clampSidebarWidth(nextWidth));
  };
  const sidebarOffset = isSidebarOpen
    ? sidebarExpandedWidth
    : SIDEBAR_COLLAPSED_WIDTH;
  const handleClose = (bannerId) => {
    setVisibleBanners((prev) => ({ ...prev, [bannerId]: false }));
  };

  if (showLoading) return <LoadingOverlay />;
  if (!isAuthenticated) {
    navigate("/login", { replace: true });
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <NavbarWithWebSocket
        isSidebarOpen={isSidebarOpen}
        sidebarOffset={sidebarOffset}
        onToggleSidebar={toggleSidebar}
      />
      <div className="flex flex-1 overflow-hidden bg-gray-50">
        <Sidebar2
          isOpen={isSidebarOpen}
          onExpandSidebar={expandSidebar}
          collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
          expandedWidth={sidebarExpandedWidth}
          minExpandedWidth={SIDEBAR_MIN_EXPANDED_WIDTH}
          maxExpandedWidth={SIDEBAR_MAX_EXPANDED_WIDTH}
          onExpandedWidthChange={handleSidebarWidthChange}
        />
        <main
          className="flex-1 min-w-0 overflow-y-auto bg-gray-50 transition-all duration-300 mt-16"
          // style={{ marginLeft: `${sidebarOffset}px` }}
        >
          <GlobalSplitViewLayout>
            <div className="min-w-0">
              {banners.map((banner) => (
                <AlertBanner
                  key={banner.id}
                  title={banner.title}
                  message={banner.message}
                  isVisible={visibleBanners[banner.id]}
                  onClose={() => handleClose(banner.id)}
                />
              ))}
              <Outlet />
              <Footer />
            </div>
          </GlobalSplitViewLayout>
        </main>
      </div>
    </div>
  );
};

export default MainLayout;
